import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import Dither from '../components/Dither/Dither';
import ProjectsPage from './ProjectsPage';
import AboutV2 from '../components/AboutV2/AboutV2';
import PublicationsList from '../components/PublicationsList/PublicationsList';
import TestimonialsSpotlight from '../components/TestimonialsSpotlight/TestimonialsSpotlight';
import DecryptedText from '../components/DecryptedText/DecryptedText';
import DitherGif from '../components/DitherGif/DitherGif';
import { initCalEmbed, CAL_LINK, CAL_NAMESPACE, CAL_CONFIG } from '../lib/calEmbed';
import './HomeV2.css';

const DECRYPT_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';

// Always New York time, regardless of the visitor's own locale/timezone —
// this is a "where I am" footer detail, not a "what time is it for you"
// one.
const NYC_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const getNycTime = () => NYC_TIME_FORMATTER.format(new Date()).toLowerCase();

interface HomeV2Props {
  theme?: string;
}

type ViewKey = 'work' | 'about' | 'publications' | 'testimonials';

// Hoisted so this array literal has one stable reference for the whole
// app lifetime. Dither's internal useEffect depends on waveColor by
// reference — passing `[0.5, 0.5, 0.5]` inline would recreate a new array
// on every HomeV2 render (e.g. every hover-driven state change elsewhere
// on the page), which tore down and restarted its animation from time=0
// each time, reading as the footer "jittering/resetting" on hover.
const DITHER_WAVE_COLOR: [number, number, number] = [0.5, 0.5, 0.5];

// Pure neutral charcoal-to-white ramp for the hover clips, matching the
// footer Dither's own plain grayscale exactly ([0.5, 0.5, 0.5], R=G=B,
// no channel split at all) — an earlier version nudged R a touch above
// G above B for warmth, which read as a visible cream/tan tint on the
// brighter clips (about, testimonials). Floor raised well above black
// (0.45 vs. a typical ~0.05 bottom-out) so even the darkest cells stay
// light — nothing in this one should read as truly dark, just a
// lighter vs. darker gray. Few stops for a coarse/blocky dither. Shared
// across all four nav items' hover clips, so the reveal reads as one
// consistent tone regardless of which item is hovered.
const NEUTRAL_PALETTE: [number, number, number][] = [
  [0.45, 0.45, 0.45],
  [0.626, 0.626, 0.626],
  [0.803, 0.803, 0.803],
  [0.98, 0.98, 0.98],
];

const LIST_ITEMS: { label: string; view: ViewKey | null }[] = [
  { label: 'work', view: 'work' },
  { label: 'publications', view: 'publications' },
  { label: 'about', view: 'about' },
  { label: 'testimonials', view: 'testimonials' },
];

const RIBBON_PADDING = 4; // px of breathing room above/below the row inside the cutout

// Same breakpoint as the <=900px rules in HomeV2.css that collapse the
// rail's nav list for mobile — the ribbon/dither hover reveal below is a
// mouse-hover effect that reads as a jarring flash on tap, so it's
// disabled entirely at this width rather than ported to touch.
const MOBILE_QUERY = '(max-width: 900px)';
const isMobileViewport = () =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;

// ProjectsPage and AboutV2 mount a lot synchronously (many cards/widgets,
// canvases, API fetches) — mounting them in the same tick as the
// ribbon-shrink tween below jank that tween. Publications/testimonials
// are cheap enough that this was never visible.
const HEAVY_VIEWS = new Set<ViewKey>(['work', 'about']);

// One consistent bezier pair for every transition tween, rather than a mix
// of power2/power3 in/out: EASE_OUT for things entering/settling, EASE_IN
// for things leaving.
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_IN = 'cubic-bezier(0.55, 0, 0.85, 0.35)';

const HomeV2: React.FC<HomeV2Props> = ({ theme = 'dark' }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeView, setActiveView] = useState<ViewKey | null>(null);
  const [renderedView, setRenderedView] = useState<ViewKey | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  // 0 = render the plain static string (no animation played yet, or the
  // back-button's instant-landing path, which should never show the
  // decrypt animation). Any increment past 0 remounts DecryptedText (via
  // its key) to play the reveal — incrementing again on every subsequent
  // trigger so repeat plays always get a fresh mount.
  const [homeRevealKey, setHomeRevealKey] = useState(0);
  const [greetingRevealKey, setGreetingRevealKey] = useState(0);
  const [nameHovered, setNameHovered] = useState(false);
  const [showPronunciation, setShowPronunciation] = useState(false);
  const [nycTime, setNycTime] = useState(() => getNycTime());
  // Mobile-only: while a panel is open, the rail's nav list collapses into
  // a hamburger (see the <=900px rules in HomeV2.css) — this tracks
  // whether tapping it has expanded the list back out as a fullscreen
  // overlay. Irrelevant above that breakpoint, where the list is always
  // visible and this stays false.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const pronunciationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const brandGreetingRef = useRef<HTMLDivElement>(null);
  const greetingLineRef = useRef<HTMLParagraphElement>(null);
  const roleLineRef = useRef<HTMLParagraphElement>(null);
  const brandHomeRef = useRef<HTMLSpanElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const transitionTlRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const interval = setInterval(() => setNycTime(getNycTime()), 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    initCalEmbed();
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'homev2-light';
    // Restore to the app's real theme state on unmount, not a snapshotted
    // className: with AnimatePresence's exit-transition delay, a DOM
    // snapshot can go stale before cleanup runs and leave body stuck on
    // homev2-light (mirrors the same guard in CacheProject.tsx).
    return () => { document.body.className = theme; };
  }, [theme]);

  const shrinkRibbonNow = (onDone?: () => void) => {
    const overlay = overlayRef.current;
    if (!overlay || activeIndex === null) {
      onDone?.();
      return;
    }
    const holeTop = overlay.style.getPropertyValue('--hole-top') || '0px';
    const holeBottom = overlay.style.getPropertyValue('--hole-bottom') || '0px';
    tweenRef.current?.kill();
    tweenRef.current = gsap.to(overlay, {
      '--ribbon-top': holeTop,
      '--ribbon-bottom': holeBottom,
      duration: 0.4,
      ease: EASE_IN,
      onComplete: () => {
        gsap.set(overlay, { opacity: 0 });
        setActiveIndex(null);
        onDone?.();
      },
    });
  };

  const handleEnter = (index: number) => {
    if (activeView) return; // nav rail is inert while a panel is open
    if (isMobileViewport()) return;
    const li = itemRefs.current[index];
    const overlay = overlayRef.current;
    if (!li || !overlay) return;

    setActiveIndex(index);

    const rect = li.getBoundingClientRect();
    const holeTop = Math.max(0, rect.top - RIBBON_PADDING);
    const holeBottom = Math.max(0, window.innerHeight - rect.bottom - RIBBON_PADDING);

    overlay.style.setProperty('--hole-top', `${holeTop}px`);
    overlay.style.setProperty('--hole-bottom', `${holeBottom}px`);

    tweenRef.current?.kill();
    gsap.set(overlay, { '--ribbon-top': `${holeTop}px`, '--ribbon-bottom': `${holeBottom}px`, opacity: 1 });
    tweenRef.current = gsap.to(overlay, {
      '--ribbon-top': '0px',
      '--ribbon-bottom': '0px',
      duration: 0.75,
      ease: EASE_OUT,
    });
  };

  const handleLeave = () => {
    if (activeView) return;
    shrinkRibbonNow();
  };

  // Everything below kicks off together the instant you click, rather than
  // waiting on each other in series: the footer slide, the ribbon shrink,
  // the greeting collapse, and the panel slide all share position 0 (or a
  // tiny stagger of it) on one timeline.
  const openView = (view: ViewKey) => {
    if (activeView === view) return;

    transitionTlRef.current?.kill();
    tweenRef.current?.kill();

    const wasAlreadyOpen = activeView !== null;
    const isHeavy = HEAVY_VIEWS.has(view);
    // Light views mount immediately as before — cheap enough that doing
    // it in the same tick as the shrink tween below was never visible.
    // Heavy views switching from another already-open panel also mount
    // immediately: the delayed-mount path further down exists only to
    // protect the ribbon-shrink tween, which doesn't run here anyway
    // (activeIndex is already null while a panel is open — see
    // handleEnter), so delaying served no purpose but latency.
    if (!isHeavy || wasAlreadyOpen) {
      setRenderedView(view);
    }
    setActiveView(view);

    const tl = gsap.timeline();
    transitionTlRef.current = tl;

    tl.to(footerRef.current, { yPercent: 100, duration: 0.5, ease: EASE_IN }, 0);

    let ribbonShrinkDuration = 0;
    if (activeIndex !== null) {
      const overlay = overlayRef.current;
      const holeTop = overlay?.style.getPropertyValue('--hole-top') || '0px';
      const holeBottom = overlay?.style.getPropertyValue('--hole-bottom') || '0px';
      ribbonShrinkDuration = 0.45;
      tl.to(overlay, {
        '--ribbon-top': holeTop,
        '--ribbon-bottom': holeBottom,
        duration: ribbonShrinkDuration,
        ease: EASE_IN,
        onComplete: () => {
          gsap.set(overlay, { opacity: 0 });
          setActiveIndex(null);
        },
      }, 0);
    }

    if (!wasAlreadyOpen) {
      // First time opening: collapse the greeting, decrypt-reveal "home".
      tl.to(brandGreetingRef.current, {
        opacity: 0,
        y: -8,
        duration: 0.35,
        ease: EASE_IN,
        onComplete: () => setHomeRevealKey((k) => k + 1),
      }, 0);
      tl.set(brandGreetingRef.current, { display: 'none' });
      tl.set(brandHomeRef.current, { display: 'inline-block' });
    }

    if (isHeavy && !wasAlreadyOpen) {
      // Let the ribbon-shrink tween finish its own window uncontested,
      // with extra breathing room after it before mounting the heavy
      // panel, then more room again before starting the slide-in so that
      // mount's synchronous commit is well clear of it rather than just
      // barely landing before it.
      tl.call(() => setRenderedView(view), [], ribbonShrinkDuration + 0.25);
      tl.call(() => setPanelOpen(true), [], ribbonShrinkDuration + 0.55);
    } else {
      tl.call(() => setPanelOpen(true), [], 0.05);
    }
  };

  // Arriving here via the project pages' back button (which passes this
  // state) should land directly in the work-open state — not play the
  // click-triggered openView('work') animation, which starts from the
  // footer/greeting's default mounted-visible state and animates them away,
  // producing a brief flash of the footer before it slides out. Setting the
  // already-open end state directly (gsap.set, not .to) skips that entirely.
  // useLayoutEffect (not useEffect) so this applies before the browser's
  // first paint — otherwise that default state still flashes for one frame
  // before a passive effect gets a chance to correct it.
  const location = useLocation();
  useLayoutEffect(() => {
    const state = location.state as { openWork?: boolean } | null;
    if (state?.openWork) {
      setRenderedView('work');
      setActiveView('work');
      setPanelOpen(true);
      gsap.set(footerRef.current, { yPercent: 100 });
      gsap.set(brandGreetingRef.current, { display: 'none', opacity: 0, y: -8 });
      gsap.set(brandHomeRef.current, { display: 'inline-block' });
      // homeRevealKey deliberately stays 0 here — it renders the plain
      // static "home" string with no DecryptedText/animation involved,
      // which is exactly what this instant-landing path wants.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goHome = () => {
    if (!activeView) return;
    transitionTlRef.current?.kill();
    setMobileNavOpen(false);

    const tl = gsap.timeline({
      onComplete: () => {
        setActiveView(null);
        setRenderedView(null);
      },
    });
    transitionTlRef.current = tl;

    tl.call(() => setPanelOpen(false), [], 0);
    tl.to(footerRef.current, { yPercent: 0, duration: 0.55, ease: EASE_OUT }, 0);
    tl.set(brandHomeRef.current, { display: 'none' }, 0);
    tl.set(brandGreetingRef.current, { display: 'block', opacity: 0, y: -8 }, 0);
    tl.to(brandGreetingRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.45,
      ease: EASE_OUT,
      onStart: () => setGreetingRevealKey((k) => k + 1),
    }, 0);
  };

  const handleNameEnter = () => {
    setNameHovered(true);
    pronunciationTimeoutRef.current = setTimeout(() => setShowPronunciation(true), 500);
  };

  const handleNameLeave = () => {
    setNameHovered(false);
    setShowPronunciation(false);
    if (pronunciationTimeoutRef.current) {
      clearTimeout(pronunciationTimeoutRef.current);
      pronunciationTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (pronunciationTimeoutRef.current) clearTimeout(pronunciationTimeoutRef.current);
    };
  }, []);

  const activeLabel = activeIndex !== null ? LIST_ITEMS[activeIndex]?.label : null;

  return (
    <div className={`homev2 ${activeView ? 'homev2--panel-open' : ''}`}>
      <div className="homev2__rail">
        <div className="homev2__brand">
          <div className="homev2__brand-greeting" ref={brandGreetingRef}>
            <p className="homev2__greeting" ref={greetingLineRef}>
              {greetingRevealKey > 0 ? (
                <DecryptedText
                  key={`greeting-${greetingRevealKey}`}
                  text="hi, i am "
                  animateOn="view"
                  sequential
                  speed={25}
                  characters={DECRYPT_CHARS}
                  className="decrypted-revealed"
                  encryptedClassName="decrypted-encrypted"
                />
              ) : 'hi, i am '}
              <span
                className={`homev2__name ${nameHovered ? 'is-hovered' : ''}`}
                onMouseEnter={handleNameEnter}
                onMouseLeave={handleNameLeave}
              >
                aditya
                {showPronunciation && (
                  <span className="homev2__name-tooltip">aah-dit-yuh</span>
                )}
              </span>
              .
            </p>
            <p className="homev2__role" ref={roleLineRef}>
              {greetingRevealKey > 0 ? (
                <DecryptedText
                  key={`role-${greetingRevealKey}`}
                  text="a product designer based in united states."
                  animateOn="view"
                  sequential
                  speed={25}
                  characters={DECRYPT_CHARS}
                  className="decrypted-revealed"
                  encryptedClassName="decrypted-encrypted"
                />
              ) : 'a product designer based in united states.'}
            </p>
          </div>
          <span
            className="homev2__brand-home"
            ref={brandHomeRef}
            onClick={goHome}
            role="button"
            tabIndex={0}
          >
            {homeRevealKey > 0 ? (
              <DecryptedText
                key={homeRevealKey}
                text="home"
                animateOn="view"
                sequential
                speed={60}
                characters={DECRYPT_CHARS}
                className="decrypted-revealed"
                encryptedClassName="decrypted-encrypted"
              />
            ) : 'home'}
          </span>
        </div>

        {/* Mobile-only (see the <=900px rules in HomeV2.css): once a
           panel is open, the two-column split leaves barely any width
           for panel content, so the full-text list hides in favor of
           this, and the list itself becomes a fullscreen tap-to-reveal
           overlay instead (is-mobile-open). Invisible/inert above that
           breakpoint. */}
        <button
          type="button"
          className="homev2__hamburger"
          aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((v) => !v)}
        >
          {mobileNavOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        <ul className={`homev2__list ${mobileNavOpen ? 'is-mobile-open' : ''}`}>
          {LIST_ITEMS.map((item, i) => (
            <li
              key={item.label}
              ref={(el) => { itemRefs.current[i] = el; }}
              onMouseEnter={() => handleEnter(i)}
              onMouseLeave={handleLeave}
            >
              {item.view ? (
                <span
                  className={`homev2__list-item ${activeView === item.view ? 'is-active' : ''}`}
                  onClick={() => { openView(item.view!); setMobileNavOpen(false); }}
                  role="button"
                  tabIndex={0}
                >
                  {item.label}
                </span>
              ) : (
                <span className="homev2__list-item">{item.label}</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className={`homev2__panel ${panelOpen ? 'is-open' : ''}`}>
        <div className="homev2__panel-inner">
          {renderedView === 'work' && <ProjectsPage theme="light" hideHeader deferredReveal />}
          {renderedView === 'about' && <AboutV2 />}
          {renderedView === 'publications' && <PublicationsList theme="light" hideHeader />}
          {renderedView === 'testimonials' && <TestimonialsSpotlight />}
        </div>
      </div>

      <div className="homev2__reveal" ref={overlayRef} aria-hidden="true" data-active={activeIndex !== null}>
        {/* Each nav item gets its own dithered clip. All four stay
           permanently mounted (not conditionally rendered) — active
           gates actual playback/decoding per instance (see DitherGif),
           so only the hovered one is doing any work, but whichever one
           gets hovered next is already primed (buffered, sized, ready to
           resume) rather than starting cold. */}
        <DitherGif
          src="/content/backdrops/work5.mp4"
          palette={NEUTRAL_PALETTE}
          active={activeLabel === 'work'}
          className={`homev2__reveal-dither ${activeLabel === 'work' ? 'is-active' : ''}`}
        />
        <DitherGif
          src="/content/backdrops/fire-burning.mp4"
          palette={NEUTRAL_PALETTE}
          active={activeLabel === 'publications'}
          className={`homev2__reveal-dither ${activeLabel === 'publications' ? 'is-active' : ''}`}
        />
        <DitherGif
          src="/content/backdrops/about3.mp4"
          palette={NEUTRAL_PALETTE}
          active={activeLabel === 'about'}
          focalY={0.5}
          className={`homev2__reveal-dither ${activeLabel === 'about' ? 'is-active' : ''}`}
        />
        <DitherGif
          src="/content/backdrops/testimonial.mp4"
          palette={NEUTRAL_PALETTE}
          active={activeLabel === 'testimonials'}
          className={`homev2__reveal-dither ${activeLabel === 'testimonials' ? 'is-active' : ''}`}
        />

        {/* Glassmorphism edge blur: reads --ribbon-top/--ribbon-bottom
           directly in CSS (the same custom properties the GSAP tween
           above already drives), so these track the growing/shrinking
           ribbon edge for free with no extra JS wiring. */}
        <div className="homev2__reveal-edge homev2__reveal-edge--top" />
        <div className="homev2__reveal-edge homev2__reveal-edge--bottom" />
      </div>

      <div className="homev2__footer" ref={footerRef}>
        <Dither
          waveColor={DITHER_WAVE_COLOR}
          disableAnimation={false}
          enableMouseInteraction={true}
          mouseRadius={0.3}
          colorNum={4}
          waveAmplitude={0.3}
          waveFrequency={3}
          waveSpeed={0.05}
        />
        <div className={`homev2__footer-top ${activeView ? 'is-hidden' : ''}`}>
          <div className="homev2__social">
            <a href="mailto:adityamanojnaik@gmail.com" aria-label="Email" className="homev2__social-mail">@</a>
            <a href="https://www.linkedin.com/in/aditya-naik-b2946a197/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
            <a href="https://x.com/adityanaik0" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="homev2__social-x"><i className="fab fa-x-twitter"></i></a>
          </div>
          <div className="homev2__clock-group">
            <div className="homev2__clock">{nycTime} NYC</div>
            <button
              type="button"
              className="homev2__cal-trigger"
              aria-label="Schedule a call"
              data-cal-link={CAL_LINK}
              data-cal-namespace={CAL_NAMESPACE}
              data-cal-config={JSON.stringify(CAL_CONFIG)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="4" rx="2" />
                <path d="M3 10h18M8 2v4M16 2v4" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeV2;
